import { requireDashboardOrigin } from "@api/features/dashboard-auth/origin/dashboardOriginGuard"
import { requireDashboardAuth } from "@api/features/dashboard-auth/session/requireDashboardAuth"
import { contentTypeNameSchema } from "@content-types"
import { serverRouteConfig } from "@server/server-route-config"
import { dashboardApiErrorMessage } from "@site-server/dashboard-api-errors"
import { createFileRoute } from "@tanstack/react-router"
import * as z from "zod"

const presignInputSchema = z.object({
  contentType: contentTypeNameSchema,
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const profileImagePresignInputSchema = z.object({
  purpose: z.literal("profileImage"),
  fileName: z.string().min(1).max(180),
  mimeType: z.string().min(1).max(100),
  size: z.number().int().positive(),
})

const dashboardAssetPresignInputSchema = z.union([profileImagePresignInputSchema, presignInputSchema])

export const Route = createFileRoute("/api/dashboard/assets/presign")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          try {
            const input = dashboardAssetPresignInputSchema.parse(await request.json())
            if ("purpose" in input && input.purpose === "profileImage") {
              const auth = await requireDashboardAuth(request, [])
              if ("response" in auth) return auth.response
              const { createDashboardProfileImageUploadData } = await import(
                "@api/features/content-assets/dashboardAssets"
              )
              const userId = auth.user.sub ?? auth.user.email ?? "dashboard-user"
              return Response.json(await createDashboardProfileImageUploadData({ ...input, userId }))
            }

            const auth = await requireDashboardAuth(request, ["admin", "editor"])
            if ("response" in auth) return auth.response
            const contentInput = presignInputSchema.parse(input)
            const { createDashboardAssetUploadData } = await import(
              "@api/features/content-assets/dashboardAssets"
            )
            return Response.json(await createDashboardAssetUploadData(contentInput))
          } catch (error) {
            return Response.json(
              { error: dashboardApiErrorMessage(error, "Could not create upload URL.") },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)
